import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { editTransactionProcessingFee } from '@openapi/transactions/transactions';
import useNotify from '../hooks/useNotify';

const TransactionProcessingFeeEditModal = ({ transaction, onSuccess, showModal, MODAL_TYPE }) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const [feeDollars, setFeeDollars] = useState(
        ((transaction?.processingFee || 0) / 100).toFixed(2),
    );
    const [loading, setLoading] = useState(false);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const handleSave = async () => {
        const dollars = parseFloat(feeDollars);
        if (Number.isNaN(dollars) || dollars < 0) {
            notifyError(t('common.positiveNumber'));
            return;
        }
        // Two-stage rounding avoids float artifacts, e.g. 1.005 * 100 = 100.4999...
        const cents = Math.round(Math.round(dollars * 1e4) / 100);
        try {
            setLoading(true);
            await editTransactionProcessingFee(transaction.id, { processingFee: cents });
            notifySuccess(t('transactions.processingFeeSaved'));
            if (onSuccess) onSuccess();
            closeAction();
        } catch (error) {
            notifyError(error?.response?.data?.message || t('errors.unknown'));
        } finally {
            setLoading(false);
        }
    };

    if (!transaction) {
        return null;
    }

    return (
        <Dialog open onClose={closeAction} maxWidth="xs" fullWidth>
            <DialogTitle>{t('transactions.editProcessingFeeTitle')}</DialogTitle>
            <DialogContent>
                <TextField
                    label={t('transactions.processingFeeLabel')}
                    type="number"
                    value={feeDollars}
                    onChange={(e) => setFeeDollars(e.target.value)}
                    fullWidth
                    autoFocus
                    margin="dense"
                    inputProps={{ min: 0, step: 0.01 }}
                    data-testid="transaction-processing-fee-field"
                />
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    buttonType="primary"
                    onClick={handleSave}
                    disabled={loading}
                    label={t('common.save')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default TransactionProcessingFeeEditModal;
